import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { SearchBarSizeEnum } from 'src/app/ui/search-bar/search-bar.component';
import { User } from 'src/app/models/user';
import { filterNotSynced, SpaceRepository } from 'src/app/state/space.repository';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import { DirectivesModule } from 'src/app/directives/directives.module';
import { CommonComponentsModule } from '../../../common/common-components.module';
import { UiModule } from '../../../ui/ui.module';
import { PipeModule } from '../../../pipes/pipes.module';

@UntilDestroy()
@Component({
  selector: 'app-participants-selection-list',
  standalone: true,
  imports: [
    CommonModule,
    UiModule,
    MatCheckboxModule,
    CommonComponentsModule,
    DirectivesModule,
    PipeModule,
  ],
  templateUrl: './participants-selection-list.component.html',
  styleUrls: ['./participants-selection-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantsSelectionListComponent implements OnInit {
  @Input() privateUsersForFrame: string[] = [];
  @Output() participantSelectionChange = new EventEmitter<string[]>();

  private readonly FILTERED_PARTICIPANTS_BATCH_SIZE = 10;
  usersRoles: { [key: string]: boolean } = {};
  filteredParticipantsUsers: User[] | undefined = []; // participants on the UI
  participantUsers: User[] | undefined; // all participants
  private shownFilteredParticipantsUsers = new BehaviorSubject<User[]>([]);
  shownFilteredParticipantsUsers$ = this.shownFilteredParticipantsUsers.asObservable();
  selectedPrivateUsersForFrame: string[] = [];
  searchBarSizeEnum = SearchBarSizeEnum;

  constructor(private spaceRepo: SpaceRepository) {}

  ngOnInit(): void {
    this.selectedPrivateUsersForFrame = [...this.privateUsersForFrame];
    this.spaceRepo.activeSpace$
      .pipe(filterNotSynced())
      .pipe(untilDestroyed(this))
      .subscribe((activeSpace) => {
        // set the user roles to know the type of the user ( host, participant )
        activeSpace.users.forEach((user) => {
          this.usersRoles[user._id] = !!user.isOwner;
        });

        this.participantUsers = activeSpace.populatedUsers.filter(
          (user) => !this.usersRoles[user._id] && activeSpace.users.find((u) => u._id === user._id),
        );

        if (this.participantUsers) {
          this.filteredParticipantsUsers = [...this.participantUsers];
          this.shownFilteredParticipantsUsers.next(this.filteredParticipantsUsers.slice(0, 10));
        }
      });
  }

  /**
   * filter results while searching for users on the UI ( search by name )
   * @param $event
   */
  onSearch($event: string): void {
    this.filteredParticipantsUsers = this.participantUsers?.filter((user) =>
      (user.name ? user.name : user.email)?.toLowerCase()?.includes($event?.toLowerCase()),
    );
    this.shownFilteredParticipantsUsers.next(
      this.filteredParticipantsUsers?.slice(0, this.FILTERED_PARTICIPANTS_BATCH_SIZE) ?? [],
    );
  }

  /**
   * load more users to show on the UI
   * we show `$FILTERED_PARTICIPANTS_BATCH_SIZE` users each time
   * and we load more when the user reaches the end of the list
   *
   * @returns void
   */
  loadMoreUsers(): void {
    const currentLength = this.shownFilteredParticipantsUsers.value.length;
    const newUsers = this.filteredParticipantsUsers?.slice(
      currentLength,
      currentLength + this.FILTERED_PARTICIPANTS_BATCH_SIZE,
    );

    this.shownFilteredParticipantsUsers.next([
      ...(this.shownFilteredParticipantsUsers.value ?? []),
      ...(newUsers ?? []),
    ]);
  }

  /**
   * handle checkbox change event
   * if it was checked, we add the user to the selected users for the private frame
   * else we remove the user from the private frame users
   * @param $event
   * @param user
   */
  handleCheckboxForUser($event: MatCheckboxChange, user: User): void {
    // handle checkbox checked
    if ($event.checked) {
      this.selectedPrivateUsersForFrame.push(user._id);
    } else {
      // handle checkbox not checked
      // remove user from selected users
      this.selectedPrivateUsersForFrame = this.selectedPrivateUsersForFrame.filter(
        (userId) => userId !== user._id,
      );
    }
    this.participantSelectionChange.emit(this.selectedPrivateUsersForFrame);
  }

  trackByUserId(index: number, user: User): string {
    return user._id;
  }
}
